/**
 * Copyright (c) 2010-2020 California CAD Solutions, Inc. Licensed subject
 * to an End-User License Agreement included in a separate file named EULA.txt
 * that is distributed with this file.
 */
import Feature from 'ol/Feature';
import Vector from 'ol/layer/Vector';
import { Vector as VectorSource } from 'ol/source';
import { fromLonLat } from 'ol/proj';
import Point from 'ol/geom/Point';

import {
    AfterViewInit,
    ChangeDetectionStrategy,
    Component,
    ElementRef,
    NgZone,
    OnDestroy,
    OnInit,
    Renderer2,
    RendererStyleFlags2,
    ViewChild,
    ViewEncapsulation,
} from '@angular/core';
import { LoginService } from '../../../core/security/services/login.service';
import { MapService } from '../services/map.service';
import { LoggerService } from '../../shared/services/logger.service';
import {
    catchError,
    concatMap,
    concatMapTo,
    delay,
    distinctUntilChanged,
    filter,
    map,
    switchMapTo,
    take,
    takeUntil,
    tap,
    timeoutWith,
    withLatestFrom,
} from 'rxjs/operators';
import { Actions, ofActionSuccessful, Select, Store } from '@ngxs/store';
import { MapConfigLoaded } from '../state/map-config.actions';
import { GoogleLayersControl } from './GoogleLayersControl';
import { GeometryCategory, GoogleBaseLayerConfig } from '../../../types';
import { MapConfigState } from '../state/map-config.state';
import { interval, Observable, of, race, Subject } from 'rxjs';
import { ActiveMapState } from '../state/active-map.state';
import { GoogleMapService } from '../services/googleMap.service';
import {
    GoogleMapInitialized,
    MapCreated,
    MapDestroyed,
    RefreshMap,
    ReloadMap,
    SetGeolocationPosition,
    SetGoogleMapType,
    SetLayerDisplayFeatures,
    SetMapZoom,
} from '../state/active-map.actions';
import { AppConfigState } from '../../../core/state/appConfig/app-config.state';
import { MapInteractionsService } from '../services/mapInteractions.service';
import { StreetViewPanoComponent } from './street-view-pano.component';
import { CustomGeoPosition, DisplayFeatures } from '../state/active-map.model';
import { isEqual } from 'lodash-es';
import { UserLocationComponent } from './user-location.component';
import { LocationService } from '../services/location.service';
import { MapConfigService } from '../services/mapConfig.service';
import { MatIconRegistry } from '@angular/material/icon';
import { DeviceService } from '../../shared/services/device.service';
import { ActivatedRoute, Params } from '@angular/router';
import { ZoomGotoConfiguration } from '../../../core/state/appConfig/app-config.model';
import { QueryService } from '../services/query.service';
import { ReportState } from '../../reports/state/report.state';
import { Navigate } from '@ngxs/router-plugin';
import { Coordinate } from 'ol/coordinate';
import { FeatureService } from '../services/feature.service';
import { DashGisUtil } from '../../shared/dash-gis-util';
import { GoogleTrafficControl } from './GoogleTrafficControl';
import { MsGroupNode } from '../state/map-config.model';

@Component({
    selector: 'dash-gis-map',
    template: `
        <div class="map-fade">
            <div id="map" #map fxLayout="column" fxFlexFill></div>
        </div>
        <dash-gis-street-view #streetView></dash-gis-street-view>
    `,
    styleUrls: ['./map.scss', './map.print.scss'],
    encapsulation: ViewEncapsulation.None,
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class MapComponent implements OnInit, OnDestroy, AfterViewInit {
    @Select(AppConfigState.loaded) isAppConfigLoaded$: Observable<boolean>;
    @Select(AppConfigState.currentMap) mapName$: Observable<string>;
    @Select(AppConfigState.enableLocation) locationEnabled$: Observable<boolean>;

    @Select(MapConfigState.loaded) mapConfigLoaded$: Observable<boolean>;
    @Select(MapConfigState.root) root$: Observable<MsGroupNode>;

    @Select(AppConfigState.baseLayer) private baseLayerConfig$: Observable<GoogleBaseLayerConfig>;
    @Select(AppConfigState.zoomGoto) private zoomGoto$: Observable<ZoomGotoConfiguration>;

    @Select(ActiveMapState.center) private center$: Observable<Coordinate>;
    @Select(ActiveMapState.displayFeatures) private displayFeatures$: Observable<DisplayFeatures>;
    @Select(ActiveMapState.position) private position$: Observable<GeolocationPosition>;

    @Select(ReportState.featureReportsLoaded) private featureReportsLoaded$: Observable<boolean>;

    @ViewChild('map', { read: ElementRef })
    private mapEl: ElementRef;

    @ViewChild('streetView', { read: StreetViewPanoComponent })
    private streetView: StreetViewPanoComponent;

    private pegmanPointerHandler: (evt: PointerEvent) => void;
    private pegmanTouchHandler: (evt: TouchEvent) => void;
    private pegmanTestRect: Partial<DOMRect>;
    private pegmanHomeRect: DOMRect;

    private pegmanObserver: MutationObserver;

    private dispose$ = new Subject<void>();

    constructor(
        private logger: LoggerService,
        private renderer: Renderer2,
        private route: ActivatedRoute,
        private device: DeviceService,
        private mapConfig: MapConfigService,
        private mapService: MapService,
        private googleMapService: GoogleMapService,
        private queryService: QueryService,
        private featureService: FeatureService,
        private locationService: LocationService,
        private actions$: Actions,
        private store: Store,
        private iconRegistry: MatIconRegistry,
        private ngZone: NgZone
    ) {}

    ngOnInit() {
        // google street view hack -- it's event handler must be in place
        // before openlayers handlers
        this.setupGoogleStreetViewHack();

        /*
         * When the component is loaded,
         *
         */
        const mapConfigLoaded$ = this.mapConfigLoaded$.pipe(filter((isLoaded) => isLoaded));

        const loadAction$ = this.actions$.pipe(
            ofActionSuccessful(MapConfigLoaded),
            concatMap(() => this.store.select(MapConfigState.loaded)),
            filter((loaded) => loaded)
        );

        this.actions$.pipe(ofActionSuccessful(MapCreated), take(1)).subscribe(() => {
            this.configureGeolocation();
            this.configureGoogleMapsLayer();
        });

        this.actions$
            .pipe(
                ofActionSuccessful(MapCreated),
                take(1),
                switchMapTo(this.featureReportsLoaded$),
                filter((b) => b),
                take(1)
            )
            .subscribe(() => {
                this.checkZoomGotoFromURL();
                this.configureLayerRefresh();
            });

        race(mapConfigLoaded$, loadAction$)
            .pipe(
                concatMapTo(this.store.select(AppConfigState.baseLayer)),
                concatMap((baseLayerConfig) => this.initializeGoogleMapsApi$(baseLayerConfig)),
                timeoutWith(2500, of(true)),
                take(1)
            )
            .subscribe(
                () => this.mapService.initMap(),
                (err) => this.logger.error('initMap subscription:', err)
            );

        this.actions$.pipe(ofActionSuccessful(SetMapZoom)).subscribe((action: SetMapZoom) => {
            this.shouldHideGoogleMap(action.payload.zoom);
        });

        this.actions$
            .pipe(
                ofActionSuccessful(RefreshMap),
                tap(() => this.refreshMap()),
                takeUntil(this.dispose$)
            )
            .subscribe();

        const tempLayer = new Vector();
        const defaultStyle = tempLayer.getStyle();
        const defaultStyleFn = tempLayer.getStyleFunction();

        // listen for changes on vector layers
        this.displayFeatures$
            .pipe(
                withLatestFrom(this.mapService.mapAvailable$),
                filter(([_, available]) => available),
                map(([features, _]) => features)
            )
            .subscribe((displayFeatures) => {
                for (const layer in displayFeatures) {
                    if (displayFeatures.hasOwnProperty(layer)) {
                        const mapLayer = this.mapService.getVectorLayer(layer);
                        if (!mapLayer) {
                            throw new Error(`Vector layer '${layer}' does not exist.`);
                        }
                        const source = mapLayer.getSource() as VectorSource;
                        source.clear(true);

                        const features: Feature[] = this.featureService.getFeatures(displayFeatures[layer]);
                        if (features.length > 0) {
                            // Group by geometry type, in case features are of different types and
                            // need different styles

                            const groupedByGeom: {
                                [key: string]: Feature[];
                            } = features.reduce((grouped, feature) => {
                                const geomName = feature.getGeometry().getType().toString();
                                (grouped[geomName] = grouped[geomName] || []).push(feature);
                                return grouped;
                            }, {});
                            Object.getOwnPropertyNames(groupedByGeom).forEach((geomName) => {
                                const geomType = this.mapService.getGeometryCategoryByTypeName(geomName);
                                const geomFeatures = groupedByGeom[geomName];

                                // check for styles
                                const style = this.mapConfig.getStyles(layer, geomType);

                                if (style) {
                                    if (
                                        mapLayer.getStyle() === defaultStyle &&
                                        mapLayer.getStyleFunction() === defaultStyleFn &&
                                        features.length > 0
                                    ) {
                                        geomFeatures.forEach((f) => f.setStyle(style));
                                    }
                                }
                                source.addFeatures(geomFeatures);
                            });
                        }
                    }
                }
            });
    }

    ngOnDestroy() {
        this.mapService.destroyMap();
        /*
         Do not attempt to destroy the google map object.
         See https://issuetracker.google.com/issues/35821412#comment32

         Instead we detach the element before it's removed (usually by logout) and reattach when
         recreating the map.
         */
        const gMap = this.googleMapService.googleMapElement;
        if (gMap) {
            const parent = gMap.parentNode;
            if (gMap.parentNode) {
                parent.removeChild(gMap);
            }
        }

        this.mapEl.nativeElement.removeEventListener('pointerdown', this.pegmanPointerHandler);
        this.mapEl.nativeElement.removeEventListener('pointermove', this.pegmanPointerHandler);
        this.mapEl.nativeElement.removeEventListener('pointerup', this.pegmanPointerHandler);
        this.mapEl.nativeElement.removeEventListener('touchstart', this.pegmanTouchHandler);
        this.mapEl.nativeElement.removeEventListener('touchmove', this.pegmanTouchHandler);
        this.mapEl.nativeElement.removeEventListener('touchend', this.pegmanTouchHandler);

        this.store.dispatch(new MapDestroyed());

        this.dispose$.next();
    }

    ngAfterViewInit() {
        // create the DOM element for the google map, or reinstate it if it was detached previously
        const mapElement = this.mapEl.nativeElement;
        if (!this.googleMapService.googleMapElement) {
            this.googleMapService.googleMapElement = this.renderer.createElement('div');
            this.googleMapService.googleMapElement.setAttribute('id', 'gmap');
        }
        if (mapElement) {
            const parent = this.renderer.parentNode(mapElement);
            if (parent) {
                this.renderer.appendChild(parent, this.googleMapService.googleMapElement);
            }
        }
    }

    /**
     * initialize google maps from defaults or map state. ok to everytime this component is created
     */
    public configureGoogleMapsLayer() {
        // this.baseLayerConfig$.subscribe((layerConfig) => {
        this.store
            .select(ActiveMapState.created)
            .pipe(
                catchError((err) => of(false)),
                distinctUntilChanged(isEqual),
                filter((b) => b),
                filter(() => this.googleMapService.apiAvailable),
                switchMapTo(this.baseLayerConfig$),
                delay(0),
                take(1)
            )
            .subscribe((layerConfig) => {
                // always reset the map type
                const googleLayerType = this.store.selectSnapshot(ActiveMapState.googleLayerType);

                this.setGoogleMapVisible(false);
                this.googleMapService.initGoogleMap(this.streetView.elementRef.nativeElement, layerConfig);
                const layers = new Map();
                layerConfig.layerTypes.forEach((layerType) => {
                    layers.set(layerType.type, {
                        callback: () => {
                            this.store.dispatch(new SetGoogleMapType(layerType.type));
                        },
                        isImagery: layerType.isImagery,
                        label: layerType.label,
                    });
                });

                const googleControl = new GoogleLayersControl({
                    renderer: this.renderer,
                    initialLayer: googleLayerType,
                    layers,
                }); // .setZone(this.ngZone);
                this.mapService.map.addControl(googleControl);
                this.googleMapService?.gmap?.setMapTypeId(googleLayerType);
                const googleTrafficControl = new GoogleTrafficControl({
                    callback: (active: boolean) => this.googleMapService.controlTrafficLayer(active),
                    labels: ['Show Traffic', 'Hide Traffic'],
                });
                this.mapService.map.addControl(googleTrafficControl);
                this.mapService.map.on('change:size', () => {
                    google.maps.event.trigger(this.googleMapService.gmap, 'resize');
                });
                this.setGoogleMapVisible(true);

                google.maps.event.addListener(this.googleMapService.gmap, 'resize', () => {
                    const intervalId = setInterval(() => {
                        if (this.findPegman()) {
                            clearInterval(intervalId);
                        }
                    }, 50);
                });

                // force google map to resize, zoom, center - in cases where we
                // are reusing a gmap object that was previously detached
                setTimeout(() => {
                    google.maps.event.trigger(this.googleMapService.gmap, 'resize');
                }, 0);
            });
    }

    private refreshMap() {
        const visibleLayers = this.mapConfig.getVisibleLayerList(this.mapService.currentScale);
        this.mapService.refreshImageWMSLayers(
            'layers',
            visibleLayers.map((l) => l.name)
        );
    }

    private configureLayerRefresh() {
        const refreshInterval = this.store.selectSnapshot(MapConfigState.refreshInterval);
        interval(refreshInterval * 1000)
            .pipe(switchMapTo(this.root$), takeUntil(this.dispose$))
            .subscribe((root) => {
                const visibleLayers = this.mapConfig.getVisibleLayerList(this.mapService.currentScale);
                const refreshLayers = visibleLayers.filter((l) => !!l.refresh);
                if (refreshLayers.length > 0) {
                    this.mapService.refreshImageWMSLayers(
                        'layers',
                        visibleLayers.map((l) => l.name)
                    );
                }
            });
    }

    private setupGoogleStreetViewHack() {
        const mapElement = document.getElementById('map');

        this.pegmanPointerHandler = this.createPegmanPointerHandler();
        this.pegmanTouchHandler = this.createPegmanTouchHandler();
        this.updatePegmanTestRect();

        const parent = mapElement.parentElement;
        if (this.device.hasPointerEvents) {
            parent.addEventListener('pointerover', this.pegmanPointerHandler, {
                passive: false,
                capture: true,
            });
            parent.addEventListener('pointerdown', this.pegmanPointerHandler, {
                passive: false,
                capture: true,
            });
            parent.addEventListener('pointermove', this.pegmanPointerHandler, {
                passive: false,
                capture: true,
            });
        }
        if (this.device.hasTouchEvents) {
            parent.addEventListener('touchstart', this.pegmanTouchHandler, {
                passive: false,
                capture: true,
            });

            parent.addEventListener('touchmove', this.pegmanTouchHandler, {
                passive: false,
                capture: true,
            });
        }
    }

    private updatePegmanTestRect() {
        const pegman = document.querySelector('.gm-svpc');
        if (pegman?.parentElement?.style.bottom) {
            const rect = pegman.getBoundingClientRect();
            if (!this.device.matchesLayoutSize('gt-md')) {
                // on a tablet/handheld, so expand the pegman rect for making touch easier
                const buffer = 8;
                this.pegmanTestRect = {
                    left: rect.left - buffer,
                    top: rect.top - buffer,
                    right: rect.right + buffer,
                    bottom: rect.bottom + buffer,
                };
            } else {
                this.pegmanTestRect = rect;
            }
        } else {
            setTimeout(() => this.updatePegmanTestRect(), 250);
        }
    }

    private createPegmanPointerHandler() {
        return (evt: PointerEvent) => {
            this.ngZone.runOutsideAngular(() => {
                if (!this.pegmanTestRect) {
                    return;
                }

                const pegman = document.querySelector<HTMLElement>('.gm-svpc');
                if (evt.target === pegman) {
                    return;
                }

                const insideRect = function (e: PointerEvent, testRect) {
                    const cX = e.clientX;
                    const cY = e.clientY;

                    return cX >= testRect.left && cX <= testRect.right && cY >= testRect.top && cY <= testRect.bottom;
                };

                if (evt.relatedTarget === pegman) {
                    this.handleDroppedPegman();
                }
            });
        };
    }

    private createPegmanTouchHandler() {
        return (evt: TouchEvent) => {
            this.ngZone.runOutsideAngular(() => {
                if (!this.pegmanTestRect) {
                    return;
                }

                const pegman = document.querySelector<HTMLElement>('.gm-svpc');
                // if (evt.target === pegman) {
                //     return;
                // }

                const insideRect = function (e: TouchEvent, testRect) {
                    const cX = (e as TouchEvent).touches.item(0).clientX;
                    const cY = (e as TouchEvent).touches.item(0).clientY;

                    return cX >= testRect.left && cX <= testRect.right && cY >= testRect.top && cY <= testRect.bottom;
                };

                if (evt.targetTouches?.length > 0 && evt.targetTouches[0].target === pegman) {
                    this.handleDroppedPegman();
                }
            });
        };
    }

    private handleDroppedPegman() {
        this.findDroppedPegman();
        // make Openlayers map less opaque to make pegman more visible
        requestAnimationFrame(() => {
            requestAnimationFrame(() => {
                this.mapEl.nativeElement.classList.add('pegman-visible');
                requestAnimationFrame(() => {
                    const cleanup = new Subject<void>();
                    let shouldWatchForClose = false;
                    this.googleMapService.streetViewPanoVisible$.pipe(takeUntil(cleanup)).subscribe((v) => {
                        if (v) {
                            shouldWatchForClose = true;
                        } else {
                            if (shouldWatchForClose) {
                                this.setPegmanClipPath();
                                this.mapEl.nativeElement.classList.remove('pegman-visible');
                                this.pegmanObserver.disconnect();
                                cleanup.next();
                            }
                        }
                    });
                    this.googleMapService.streetViewPositionChanged$.pipe(takeUntil(cleanup)).subscribe(() => {
                        // use RAF twice because the pegman element will move during the first RAF after
                        // the position_changed event fires
                        requestAnimationFrame(() => {
                            requestAnimationFrame(() => {
                                const pegmanDropped = document.querySelector<HTMLElement>(
                                    '#gmap .gm-style div[class*="marker-view"] div[class*="-interactive"] > div > div'
                                );
                                this.setPegmanClipPath(pegmanDropped);
                            });
                        });
                    });
                });
            });
        });
    }

    private findPegman(): boolean {
        // google street view hack
        const pegman = document.querySelector<HTMLElement>('.gm-svpc');

        if (!pegman?.parentElement?.style.bottom) {
            return false;
        }

        this.pegmanHomeRect = pegman.getBoundingClientRect();
        setTimeout(() => this.setPegmanClipPath(), 100);
        return true;
    }

    private findDroppedPegman(): void {
        // add ability to add a clip-path to the map image layer so the pegman doesn't get buried under imagery layers
        // selector for top element: '#gmap .gm-style' (this is as close as we can get with the identifiers google leaves
        // selector for descendant: 'div[class*="marker-view"] div[class*="-interactive"] > div > div'

        new Promise<HTMLElement>((resolve, reject) => {
            const intervalId = setInterval(() => {
                const el: HTMLElement = document.querySelector('#gmap .gm-style');
                if (el) {
                    clearInterval(intervalId);
                    resolve(el);
                }
            }, 100);
        }).then((observedElement) => {
            const observerConfig: MutationObserverInit = {
                attributes: false,
                childList: true,
                subtree: true,
            };
            if (!this.pegmanObserver) {
                this.pegmanObserver = new MutationObserver((mutations, obs) => {
                    const pegmanDropped = document.querySelector(
                        '#gmap .gm-style div[class*="marker-view"] div[class*="-interactive"] > div > div'
                    );
                    if (pegmanDropped) {
                        this.setPegmanClipPath(pegmanDropped);
                        return;
                    }
                });
            }
            this.pegmanObserver.observe(observedElement, observerConfig);
        });
    }

    private setPegmanClipPath(pegman2?: Element) {
        const mapRect = this.mapEl.nativeElement.getBoundingClientRect() as DOMRectReadOnly;

        // bounding client rects are relative to viewport, while path points must be relative to
        // the element (i.e., path start x/y must be zero, so we grab the current x and y for the map
        // element rect, and subtract where appropriate

        // we also have to account for old Edge browser's nonstandard ClientRect, which
        // has no x/y values
        const xOffset = mapRect.left;
        const yOffset = mapRect.top;
        const mp = {
            x: 0,
            y: 0,
            width: mapRect.width,
            height: mapRect.height,
        };
        const moat = 0;
        const pp = {
            x: this.pegmanHomeRect.left - xOffset - moat,
            y: this.pegmanHomeRect.top - yOffset - moat,
            width: this.pegmanHomeRect.width + 2 * moat,
            height: this.pegmanHomeRect.height + 2 * moat,
        };

        const pathSpec = [
            `M${mp.x},${mp.y} V${mp.height} H${mp.width}`,
            `L${pp.x + pp.width},${pp.y + pp.height} h-${pp.width} v-${pp.height}`,
        ];

        if (pegman2) {
            const p2Rect = pegman2.getBoundingClientRect() as DOMRectReadOnly;
            const p2 = {
                x: p2Rect.left - xOffset - moat,
                y: p2Rect.top - yOffset - moat,
                width: p2Rect.width + 2 * moat,
                height: p2Rect.height + 2 * moat,
            };
            pathSpec.push(
                `L${p2.x + p2.width / 2},${p2.y + p2.height}`,
                `A${p2.width / 2},${p2.height / 2},0,1,1,${p2.x + p2.width / 2},${p2.y}`,
                `A${p2.width / 2},${p2.height / 2},0,1,1,${p2.x + p2.width / 2},${p2.y + p2.height}`,
                `L${pp.x},${pp.y}`
            );
        }

        pathSpec.push(
            `h${pp.width} v${pp.height}`,
            `L${mp.x + mp.width},${mp.y + mp.height} V-${mp.height} H-${mp.width}`
        );

        const path = pathSpec.join(' ');
        const clipStyle = `path('${path}')`;
        this.mapEl.nativeElement.style.clipPath = clipStyle;
    }

    private checkZoomGotoFromURL() {
        this.route.queryParams
            .pipe(
                take(1),
                withLatestFrom(this.zoomGoto$),
                tap(([params, zg]: [Params, ZoomGotoConfiguration]) => {
                    const layer = params['layer']?.toLowerCase();
                    const found = Object.keys(zg.layers).find((f) => f.toLowerCase() === layer);
                    if (found && params['key']) {
                        this.queryService.zoomTo(found, zg.layers[found].key, params['key']);
                    }
                })
            )
            .subscribe(() =>
                this.store.dispatch(
                    new Navigate(
                        [],
                        {
                            layer: null,
                            key: null,
                        },
                        { queryParamsHandling: 'merge' }
                    )
                )
            );
    }

    private configureGeolocation() {
        const enabled$ = this.locationEnabled$;

        // update the store with the position from geolocation api
        enabled$
            .pipe(
                filter((b) => b),
                take(1),
                switchMapTo(this.locationService.position$),
                take(1)
            )
            .subscribe((position: GeolocationPosition) => {
                const payload: CustomGeoPosition = {
                    latitude: position.coords.latitude,
                    longitude: position.coords.longitude,
                };
                this.store.dispatch(new SetGeolocationPosition(payload));
            });

        // add the control to zoom to the location
        enabled$
            .pipe(
                filter((b) => b),
                take(1),
                switchMapTo(this.iconRegistry.getNamedSvgIcon('gps'))
            )
            .subscribe((svg: SVGElement) => {
                const options = {
                    callback: (position: GeolocationPosition) => {
                        // convert from lat/lon to map coords
                        const geoCoords: Coordinate = [position.coords.longitude, position.coords.latitude];
                        const mapCoords = fromLonLat(geoCoords, this.mapService.projection);
                        this.mapService.map.getView().setCenter(mapCoords);
                    },
                    svg,
                };
                const locationControl = new UserLocationComponent(options);
                this.mapService.map.addControl(locationControl);
            });

        const locationStyles = this.mapConfig.getStyles('location', GeometryCategory.Point);

        // finally, create the feature to display
        this.position$
            .pipe(
                distinctUntilChanged(isEqual),
                map((position) => fromLonLat([position.longitude, position.latitude], this.mapService.projection)),
                takeUntil(this.actions$.pipe(ofActionSuccessful(MapDestroyed))),
                tap((coords: Coordinate) => {
                    const geom = new Point(coords);
                    const featureId = `Geolocation.${DashGisUtil.uid()}`;
                    const feature = new Feature(geom);
                    feature.setId(featureId);
                    feature.setStyle(locationStyles);
                    this.featureService.addFeature(feature);
                    setTimeout(
                        () =>
                            this.store.dispatch(
                                new SetLayerDisplayFeatures({
                                    displayLayer: 'location',
                                    featureIds: [featureId],
                                })
                            ),
                        0
                    );
                })
            )
            .subscribe();
    }

    /**
     * Inserts the script tag to load google maps. Should only be run once!!
     * @returns {Observable<boolean>}
     * @param config {GoogleBaseLayerConfig}
     */
    private initializeGoogleMapsApi$(config: GoogleBaseLayerConfig): Observable<boolean> {
        if (!(typeof google === 'object' && typeof google.maps === 'object') && config.type === 'google') {
            const gmapSubject$ = new Subject<boolean>();
            const apiKey = config.apiKey;
            if (apiKey && !((window as any).google && (window as any).google.maps)) {
                (window as any).initGmap = () => {
                    this.store
                        .dispatch(new GoogleMapInitialized())
                        .pipe(take(1))
                        .subscribe(() => gmapSubject$.next(true));
                };
                const script = window.document.createElement('script');
                const googleApiUrl = new URL('https://maps.googleapis.com/maps/api/js');
                googleApiUrl.searchParams.append('v', '3');
                googleApiUrl.searchParams.append('key', apiKey);
                googleApiUrl.searchParams.append('callback', 'initGmap');
                googleApiUrl.searchParams.append('libraries', 'places,geometry');
                script.src = googleApiUrl.toString();
                window.document.body.appendChild(script);
            }
            return gmapSubject$.asObservable();
        } else {
            return of(true);
        }
    }

    private shouldHideGoogleMap(zoom: number) {
        if (this.googleMapService.googleMapElement) {
            const mapType = this.store.selectSnapshot(ActiveMapState.googleLayerType);
            const baseLayerConfig = this.store.selectSnapshot(AppConfigState.baseLayer);
            const maxZoom = this.store.selectSnapshot(ActiveMapState.googleMaxZoom);
            const layerType = baseLayerConfig.layerTypes.find((x) => x.type === mapType);

            if (zoom > maxZoom && !layerType.isImagery) {
                this.setGoogleMapVisible(false);
            } else {
                this.setGoogleMapVisible(true);
            }
        }
    }

    private setGoogleMapVisible(visible) {
        if (visible) {
            this.renderer.removeAttribute(this.googleMapService.googleMapElement, 'hidden');
            this.googleMapService.refreshDisplay();
        } else {
            this.renderer.setAttribute(this.googleMapService.googleMapElement, 'hidden', 'hidden');
        }
    }
}
